// src/components/common/CVImportBadge.jsx
//
// Badge affichant les infos d'import d'un CV :
//   - Date d'import
//   - Auteur (résolu via useUsersMap)
//   - Indicateur d'ancienneté visuel : Récent (<3 mois), À actualiser (3-6 mois),
//     Ancien (6-12 mois), Très ancien (>12 mois)
//
// Compact par défaut (pour s'afficher inline à côté d'un lien CV).
//
// Usage :
//   <CVImportBadge
//     importedAt={cv.created_at || candidate.added_at}
//     importedById={cv.utilisateur_id || candidate.added_by_user_id}
//   />

import React from 'react';
import { FileText, Clock, AlertTriangle } from 'lucide-react';
import { useUsersMap } from '../../hooks/useUsersMap';
import './CVImportBadge.css';

// Calcule l'ancienneté du CV en mois (approx)
function monthsSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));
}

// Renvoie { level, label, color, bg } selon l'ancienneté
function getFreshness(months) {
  if (months === null) return null;
  if (months < 3)  return { level: 'fresh',     label: 'Récent',         color: '#16A34A', bg: 'rgba(34,197,94,0.10)' };
  if (months < 6)  return { level: 'ok',        label: 'À actualiser',   color: '#0EA5E9', bg: 'rgba(14,165,233,0.10)' };
  if (months < 12) return { level: 'old',       label: 'Ancien',         color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' };
  return                  { level: 'very-old',  label: 'Très ancien',    color: '#DC2626', bg: 'rgba(220,38,38,0.10)' };
}

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CVImportBadge({
  importedAt,
  importedById,
  importedByName,
  compact = true,
  showIfNoData = false,
}) {
  const { getUser } = useUsersMap();

  const dateStr  = formatDate(importedAt);
  const months   = monthsSince(importedAt);
  const freshness = getFreshness(months);

  // Priorité : résolution via user_id (fiable car c'est un vrai User CRM)
  // Fallback : nom_proprietaire texte libre (souvent du bruit côté backend)
  // Filtre anti-bruit : on rejette les valeurs qui ressemblent à un titre de poste
  const resolvedFromId = importedById ? getUser(importedById)?.name : null;
  const looksLikeJobTitle = importedByName && (
    importedByName.includes('/') ||
    importedByName.length > 60 ||
    /senior|junior|engineer|developer|manager|lead/i.test(importedByName)
  );
  const user = resolvedFromId
    || (importedByName && !looksLikeJobTitle ? importedByName : null);

  // Pas de data du tout
  if (!dateStr) {
    if (!showIfNoData) return null;
    return (
      <span className="cv-import-badge cv-import-badge--empty">
        <FileText size={12} />
        Import CV non daté
      </span>
    );
  }

  const isOld = freshness && (freshness.level === 'old' || freshness.level === 'very-old');

  return (
    <div className={`cv-import-badge ${compact ? 'cv-import-badge--compact' : ''}`}>
      <span className="cv-import-badge-date">
        {isOld ? <AlertTriangle size={12} /> : <Clock size={12} />}
        <span className="cv-import-badge-label">Importé le</span>
        <strong>{dateStr}</strong>
        {user && (
          <>
            <span className="cv-import-badge-sep">·</span>
            <span className="cv-import-badge-author">par <strong>{user}</strong></span>
          </>
        )}
      </span>
      {freshness && (
        <span
          className="cv-import-badge-freshness"
          style={{ color: freshness.color, background: freshness.bg, border: `1px solid ${freshness.color}33` }}
          title={`CV importé il y a ${months} mois`}
        >
          {freshness.label}
        </span>
      )}
    </div>
  );
}
